export interface ChangelogEntry {
  version: string
  date: string
  title: string
  categories: {
    type: 'feature' | 'improvement' | 'fix'
    items: string[]
  }[]
}

export const changelog: ChangelogEntry[] = [
  {
    version: '1.10.0',
    date: '2026-06-12',
    title: 'Custom Headers for HTTP Tools',
    categories: [
      {
        type: 'feature',
        items: [
          'Send bot variables as custom headers on HTTP tools — pick a variable from the tool form and it is sent as a header on every call',
        ],
      },
      {
        type: 'fix',
        items: [
          'Editing an HTTP tool in the dashboard no longer removes custom headers that were set via the API',
          'Chatbot update API now accepts the settings field as documented — define bot variables directly via the API',
        ],
      },
    ],
  },
  {
    version: '1.9.2',
    date: '2026-04-28',
    title: 'Data Source API & Security',
    categories: [
      {
        type: 'feature',
        items: [
          'Bulk delete data sources in a single request (DELETE /v1/data-sources?ids=...)',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Data source list endpoint now returns custom_metadata on every item',
          'New filter params on data source list: status, filename, tag, date range',
          'Sort and include options on data source list (?sort=upload_date:asc, ?include=content)',
          'Original text content is now preserved and retrievable after training',
          'Stricter validation on webhook endpoint registration',
        ],
      },
    ],
  },
  {
    version: '1.9.1',
    date: '2026-04-10',
    title: 'API Log Inspector Polish',
    categories: [
      {
        type: 'improvement',
        items: [
          'API log request and response bodies now render as syntax-highlighted JSON with proper indentation',
          'Nested stringified JSON (like training content payloads) is automatically unwrapped for readability',
          'Added Pretty/Raw toggle, line wrap toggle, and size/line count indicators to log detail panel',
          'Widened the log detail panel and removed aggressive word-breaking that was mangling JSON output',
        ],
      },
      {
        type: 'fix',
        items: [
          'Arabic, Chinese, and other non-ASCII content in API logs now displays correctly instead of showing Unicode escape sequences',
          'API logs now capture the full request and response body — removed the previous size cap so large payloads are no longer cut off',
          'Fixed text training crashing with "\'list\' object has no attribute \'get\'" when the chunking response came back as a bare list instead of the expected object',
        ],
      },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-04-02',
    title: 'Account Security & Password Reset',
    categories: [
      {
        type: 'feature',
        items: [
          'Password reset via email — request a reset link from the login page',
          'Email verification for new accounts in production',
          'Account lockout after repeated failed login attempts (auto-unlocks after 15 minutes)',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Forgot password page with clear user feedback',
          'Password strength meter on the reset password form',
          'OAuth users receive a helpful email if they try to reset a password',
        ],
      },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-03-30',
    title: 'API Log Inspector & Metadata Validation',
    categories: [
      {
        type: 'feature',
        items: [
          'API request log detail panel — inspect full request and response bodies for any API call',
          'Request and response body capture for all API calls (JSON)',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Data source metadata field renamed to custom_metadata for consistency',
          'System-managed metadata fields are now protected from accidental overwrites',
          'Custom metadata validation: max 30 keys, 64-char key names, 1000-char values, simple types only',
          'Metadata updates now correctly merge with existing data instead of replacing it',
        ],
      },
      {
        type: 'fix',
        items: [
          'Fixed data source metadata updates failing with a server error',
          'Fixed metadata sync overwriting existing document data on update',
        ],
      },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-03-27',
    title: 'Data Source Metadata & Reliability Fixes',
    categories: [
      {
        type: 'feature',
        items: [
          'Update data source metadata without re-uploading (PATCH /v1/data-sources/{id})',
          'New webhook event: training.data_source_updated',
          'Changelog page added to dashboard under Resources',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Smarter conversation memory — tool result previews compressed after first turn',
        ],
      },
      {
        type: 'fix',
        items: [
          'Fixed training sources showing "Failed" when successfully processed',
          'Fixed mixed content HTTPS errors across 10 frontend files',
          'Fixed HTTPS redirect loops in production (proxy header trust)',
        ],
      },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-03-23',
    title: 'Faster Training Pipeline',
    categories: [
      {
        type: 'improvement',
        items: [
          'Files start processing immediately — no waiting behind other users',
          'Document chunking is now ~5x faster per file',
          'Embedding generation is now ~25x faster per file',
          'URLs are now processed in parallel instead of one by one',
          'Increased concurrent file processing from 3 to 5 per user',
        ],
      },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-03-07',
    title: 'Suggestions Page & Agent Sidebar Navigation',
    categories: [
      {
        type: 'feature',
        items: [
          'Suggestions page for reviewing AI-generated bot improvement ideas',
          'Wired Suggestions into agent sidebar navigation',
        ],
      },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-03-06',
    title: 'Prompt Caching & Model Pricing Audit',
    categories: [
      {
        type: 'feature',
        items: [
          'Prompt caching support for Anthropic and OpenAI providers',
          'Cache-aware pricing calculations for accurate cost tracking',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Comprehensive model pricing audit and update across all providers',
          'Added automated pricing update script for future maintenance',
        ],
      },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-03-05',
    title: 'SSE Streaming Enhancements & Cross-Model Safety',
    categories: [
      {
        type: 'feature',
        items: [
          'Tool event streaming via SSE (tool-start, tool-end events)',
          'WebSocket mirror for real-time streaming updates',
          'Stream cancellation support for all endpoints',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Cross-model safety improvements for multi-provider setups',
          'Enhanced SSE retry logic with exponential backoff',
        ],
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-03-01',
    title: 'Auto-Learning & Agent Memory',
    categories: [
      {
        type: 'feature',
        items: [
          'Auto-learning extracts memories from conversations automatically',
          'Persistent agent memory across sessions (save, recall, delete)',
          'Bot suggestions extracted from conversation patterns',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Memory scoping via session variables with brain toggle UI',
          'Pre-compaction memory flush to preserve facts before summarization',
        ],
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-02-25',
    title: 'Smart Context Compaction',
    categories: [
      {
        type: 'feature',
        items: [
          'Configurable compaction modes: sliding window and LLM summary',
          'Adjustable memory length (10/20/40/60 messages)',
          'Message pinning preserves emails, IDs, and tool results',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Incremental summaries with round tracking',
          'Token-accurate estimation via tiktoken',
        ],
      },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-02-20',
    title: 'Upload Security & Storage Quotas',
    categories: [
      {
        type: 'fix',
        items: [
          'Hardened file upload limits (max 20 files per batch)',
          'Added MaxRequestSizeMiddleware for all upload endpoints',
          'Storage quota enforcement with plan-based limits',
        ],
      },
      {
        type: 'improvement',
        items: [
          'Chunked streaming for large file uploads',
          'Base64 upload size caps for API v1',
        ],
      },
    ],
  },
]
